/**
 * Level-up celebration trigger (specs/xp-and-ranks AC-15/AC-16). The device remembers the last
 * CELEBRATED level per user; a fetched summary showing a higher level fires the "Number Slam"
 * level-up toast. First sync on a device records silently.
 *
 * RANK-up is NO LONGER handled here: rank is coin-based (specs/coin-based-ranks) and driven from
 * the coin store via `coinRankCelebrations.helper.ts` / `useCoinRankCelebrations`, because coin
 * gains that award no XP never refetch this summary. Level and rank celebrate independently now.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../../helpers/logger.helper';
import { useCelebrationStore } from '../stores/celebrationStore';
import type { XpSummary } from './xpApi';

const key = (userId: string) => `xp_last_celebrated_level_${userId}`;

export async function readLastCelebratedLevel(userId: string): Promise<number | null> {
  try {
    const raw = Platform.OS === 'web' && typeof window !== 'undefined'
      ? localStorage.getItem(key(userId))
      : await AsyncStorage.getItem(key(userId));
    const parsed = raw == null ? NaN : Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (error) {
    if (__DEV__) logger.warn('xpCelebrations: read failed', { error });
    return null;
  }
}

export async function writeLastCelebratedLevel(userId: string, level: number): Promise<void> {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      localStorage.setItem(key(userId), String(level));
      return;
    }
    await AsyncStorage.setItem(key(userId), String(level));
  } catch (error) {
    if (__DEV__) logger.warn('xpCelebrations: write failed', { error });
  }
}

/**
 * Compare a fetched summary with the stored level and fire the level-up toast on an increase.
 * Rank changes are handled separately (coin-based). Exposed as a pure-ish function for testability.
 */
export async function maybeCelebrateXp(userId: string, summary: XpSummary): Promise<void> {
  const stored = await readLastCelebratedLevel(userId);

  if (stored == null || stored > summary.level) {
    // First sync (record silently) or a backfill/reset lowered the level (re-baseline).
    await writeLastCelebratedLevel(userId, summary.level);
    return;
  }
  if (summary.level <= stored) return;

  useCelebrationStore.getState().showLevelUp({
    level: summary.level,
    xpCurrent: summary.xpIntoLevel,
    xpTarget: summary.xpForLevel,
  });
  await writeLastCelebratedLevel(userId, summary.level);
}
